import { ActiveSearchTags } from '@/components/ActiveSearchTags';
import { CollapsableSearch } from '@/components/CollapsableSearch';
import { useLoginInfo } from '@/components/LoginInfo/provider';
import { ErrorBoundary } from '@/components/Rollbar';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import {
  ACTION_BUTTONS_GAP,
  EMPTY_PAYEE_ARRAY,
  EXCLUDED_ACTIVE_TAG_FIELDS,
  INVOICE_REPORTS_ALWAYS_SHOW_FIELDS,
} from '@/features/invoice_reports/constants';
import type { InvoiceReportsSearchQuery } from '@/features/invoice_reports/types';
import { useShowSearchValue } from '@/hooks/invoice_reports/useShowSearchValue';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import type { OfficeInvoiceReportSettings } from 'ap-openapi';
import classnames from 'classnames/bind';
import { FC, memo, Suspense, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ErrorFallback } from './components/ErrorFallback';
import { SearchForm } from './components/SearchForm';
import {
  InvoiceReportsSearchFormData,
  useAgentApplicantInitialData,
  useApPayeeInitialData,
  useConfigForm,
  useInvoiceReportsFieldOrders,
  useMergedOptions,
  usePaymentStatusTransformer,
  useSearchForm,
} from './hooks';
import styles from './Search.module.scss';

const cx = classnames.bind(styles);

interface SearchProps {
  defaultValues?: InvoiceReportsSearchFormData;
  onSearch: (data: InvoiceReportsSearchQuery) => void;
  searchQuery?: InvoiceReportsSearchQuery;
  isLoading?: boolean;
  officeInvoiceReportSettings?: OfficeInvoiceReportSettings;
}

export const InnerSearch: FC<SearchProps> = memo(
  ({
    defaultValues,
    onSearch,
    searchQuery,
    isLoading,
    officeInvoiceReportSettings,
  }) => {
    const { t } = useTranslation();
    const form = useForm<InvoiceReportsSearchFormData>({
      defaultValues,
      mode: 'all',
    });
    const canAgentEntry =
      officeInvoiceReportSettings?.office_member_setting?.can_agent_entry ??
      false;

    // Use stable array references for hook inputs
    const payeeIds = useMemo(
      () =>
        defaultValues?.ap_payee_id
          ? [defaultValues.ap_payee_id]
          : EMPTY_PAYEE_ARRAY,
      [defaultValues?.ap_payee_id]
    );

    const agentApplicantIds = useMemo(
      () => defaultValues?.agent_applicant_office_member_ids ?? [],
      [defaultValues?.agent_applicant_office_member_ids]
    );

    const payeeOptions = useApPayeeInitialData(payeeIds);
    const agentApplicantOptions =
      useAgentApplicantInitialData(agentApplicantIds);

    const { customProps, defaultOptions, checkLengthValidationRules } =
      useConfigForm({
        officeInvoiceReportSettings,
      });

    const appPayeeDefaultOptions = useMergedOptions<BaseOption>(
      defaultOptions.apPayeeDefaultOptions,
      payeeOptions
    );

    const { currentOfficeMember } = useLoginInfo();
    const { handleReset } = useSearchForm({ form });

    // Use primitive dependencies for sort fields
    const sortFields = defaultValues?.sort_fields;
    const sortOrders = defaultValues?.sort_orders;

    const onHandleSearch = useCallback(
      (data: InvoiceReportsSearchFormData) => {
        const {
          status,
          mf_file,
          payment_status,
          agent_applicant_office_member_ids,
          ...rest
        } = data;
        onSearch({
          ...rest,
          status: status === '' || status === null ? undefined : status,
          mf_file: mf_file === '' || mf_file === null ? undefined : mf_file,
          payment_status:
            payment_status === '' || payment_status === null
              ? undefined
              : payment_status,
          ...(canAgentEntry &&
            agent_applicant_office_member_ids?.length && {
              agent_applicant_office_member_ids,
            }),
          sort_fields: sortFields,
          sort_orders: sortOrders,
        });
      },
      [onSearch, sortFields, sortOrders, canAgentEntry]
    );

    /** Exclude agent tag when canAgentEntry is false  */
    const excludeFields = useMemo(() => {
      const fields = [...EXCLUDED_ACTIVE_TAG_FIELDS];
      if (!canAgentEntry) {
        fields.push('agent_applicant_office_member_ids');
      }
      return fields;
    }, [canAgentEntry]);

    const { FIELD_ORDERS } = useInvoiceReportsFieldOrders();
    const customTransformers = usePaymentStatusTransformer();

    const [actionButtonsWidth, setActionButtonsWidth] = useState(0);
    const headerStyle = useMemo<Record<string, string>>(
      () => ({
        '--action-buttons-space': `${
          actionButtonsWidth + ACTION_BUTTONS_GAP
        }px`,
      }),
      [actionButtonsWidth]
    );

    const { searchValuesShow } = useShowSearchValue({
      searchQuery,
      fieldOrders: FIELD_ORDERS,
      payeeOptions: appPayeeDefaultOptions,
      excludeFields,
      customTransformers,
      alwaysShowFields: INVOICE_REPORTS_ALWAYS_SHOW_FIELDS,
    });

    // Memoize the render callback for CollapsableSearch
    const renderSearchForm = useCallback(
      () => (
        <SearchForm
          agentApplicantDefaultOptions={agentApplicantOptions}
          appPayeeDefaultOptions={appPayeeDefaultOptions}
          canAgentEntry={canAgentEntry}
          customProps={customProps}
          checkLengthValidationRules={checkLengthValidationRules}
          form={form}
          onSubmit={onHandleSearch}
          onReset={handleReset(canAgentEntry, currentOfficeMember?.id)}
          isLoading={isLoading}
          onActionButtonsWidthChange={setActionButtonsWidth}
        />
      ),
      [
        agentApplicantOptions,
        appPayeeDefaultOptions,
        canAgentEntry,
        currentOfficeMember?.id,
        customProps,
        checkLengthValidationRules,
        form,
        onHandleSearch,
        handleReset,
        isLoading,
        setActionButtonsWidth,
      ]
    );

    return (
      <div className={cx('search-container')}>
        <div className={cx('header')} style={headerStyle}>
          <CollapsableSearch
            initialOpen
            openTitle={t('invoice_reports.search.collapse_title')}
            closeTitle={t('invoice_reports.search.expand_title')}
            sectionTitleExpandedClassName={cx('section-title-expanded')}
          >
            {renderSearchForm}
          </CollapsableSearch>
        </div>
        <ActiveSearchTags activeSearchValues={searchValuesShow} />
      </div>
    );
  }
);
InnerSearch.displayName = 'InnerSearch';

// Memoized error fallback render function
const renderErrorFallback = ({ error }: { error: Error | null }) => (
  <ErrorFallback error={error} />
);

export const Search: FC<SearchProps> = memo(
  ({
    defaultValues,
    onSearch,
    searchQuery,
    isLoading,
    officeInvoiceReportSettings,
  }) => (
    <Suspense
      fallback={<SuspenseLoading className={cx('search-container-loading')} />}
    >
      <ErrorBoundary fallback={renderErrorFallback}>
        <InnerSearch
          defaultValues={defaultValues}
          onSearch={onSearch}
          searchQuery={searchQuery}
          isLoading={isLoading}
          officeInvoiceReportSettings={officeInvoiceReportSettings}
        />
      </ErrorBoundary>
    </Suspense>
  )
);
Search.displayName = 'Search';
